<template>
  <div class="container-fluid">
    <h1>Sales by Location</h1>

    <b-form-row>
      <b-col cols=1>
        <label>Country</label>
      </b-col>
      <b-col>
        <b-form-select
          required
          v-if="countries"
          :disabled="disableEditing"
          :options="niceCountries"
          v-model="country"
          @input="handleChangeCountry"
        />
      </b-col>
      <b-col cols=1>
        <label>State</label>
      </b-col>
      <b-col>
        <b-form-select
          v-if="country && countries[country].states"
          required
          :disabled="disableEditing"
          :options="niceStates"
          v-model="state"
        />
        <b-form-select
          v-else
          :disabled="disableEditing"
          :options="[{ text:'No State', value: null }]"
          v-model="state"
        />
      </b-col>
      <b-col cols=1>
        <label>Start</label>
      </b-col>
      <b-col>
        <b-input v-model="start" type='date' class='mb-2 ml-1 mr-2 mb-sm-0'/>
      </b-col>
      <b-col cols=1>
        <label>Finish</label>
      </b-col>
      <b-col>
        <b-input v-model="finish" type='date' class='mb-2 ml-1 mr-2 mb-sm-0'/>
      </b-col>
      <b-col cols=1>
        <b-button variant="success" @click="getResults">Submit</b-button>
      </b-col>
    </b-form-row>
    <b-row v-if="results.length > 0" class="my-4">
      <b-col><strong># of Orders: </strong>{{results.length}}</b-col>
      <b-col><strong>Pre-Tax: </strong>${{ preTaxSum }}</b-col>
      <b-col><strong>Collected Tax: </strong>${{ taxSum }}</b-col>
      <b-col><strong>Grand Total: </strong>${{ totalSum }}</b-col>
    </b-row>
    <b-table
      striped
      :items="results"
      :sort-by="'num'" 
      :sort-compare="sortCompare"
      :fields="columns"
      v-if="results.length > 0"
    >
      <template slot="num" slot-scope="data">
        <router-link :to="'/sales-order/view/' + data.item.num">
          {{ data.item.num }}
        </router-link>
      </template>
      <template slot="subTotal" slot-scope="data">
        ${{ Math.round(Number(data.item.subTotal) * 100) / 100 }}
      </template>
      <template slot="totalTax" slot-scope="data">
        ${{ Math.round(Number(data.item.totalTax) * 100) / 100 }}
      </template>
      <template slot="totalPrice" slot-scope="data">
        ${{ Math.round(Number(data.item.totalPrice) * 100) / 100 }}
      </template>
      <template slot="dateCompleted" slot-scope="data">
        <NiceDate :value="data.item.dateCompleted" :showTime="false" /> 
      </template>
      <template slot="dateIssued" slot-scope="data">
        <NiceDate :value="data.item.dateIssued" :showTime="false" /> 
      </template>
      <template slot="dateFirstShip" slot-scope="data">
        <NiceDate :value="data.item.dateFirstShip" :showTime="false" /> 
      </template>
      <template slot="country" slot-scope="data">
        {{data.item.country}} / {{data.item.state}}
      </template>
    </b-table>

    <b-modal id="statusModal" ref="statusModalRef" centered hide-header hide-footer>
      <div class="d-block text-center">Loading...</div>
      <center>
        <fingerprint-spinner :animation-duration="1500" :size="300" color="#293a93" class="my-3" />
      </center>
      <div slot="modal-footer" class="w-100" />
  </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import { FingerprintSpinner } from 'epic-spinners';
import NiceDate from '../../utility/NiceDate';

let BASE_API = process.env.VUE_API_ROOT;

export default {
  data() {
    return {
      country: 'US',
      state: 'IN',
      start: '',
      finish: '',
      results: [],
      disableEditing: false,
      columns: [
        { key: 'num', label: 'Order', sortable: true },
        { key: 'subTotal', label: 'PreTax', sortable: true },
        { key: 'totalTax', label: 'Tax', sortable: true },
        { key: 'totalPrice', label: 'Total', sortable: true },
        { key: 'dateIssued', label: 'Issued On', sortable: true },
        { key: 'dateFirstShip', label: 'Ship Started On', sortable: true },
        { key: 'dateCompleted', label: 'Completed On', sortable: true },
        { key: 'country', label: 'Country / State', sortable: true },
      ]
    };
  },
  components: {
    FingerprintSpinner,
    NiceDate
  },
  computed: {
    ...mapGetters({
      countries: 'countries',
    }),
    preTaxSum() {
      return this.mappedSum( (e) => Number(e.subTotal) );
    },
    taxSum() {
      return this.mappedSum( (e) => Number(e.totalTax) );
    },
    totalSum() {
      return this.mappedSum( (e) => Number(e.totalPrice) );
    },
    niceCountries() {
      if(this.countries) {
        return Object.keys(this.countries).map((abbr) => {
          return {text: `${abbr} | ${this.countries[abbr].name}`, value: abbr}
        });
      } else {
        return {}
      }
    },
    niceStates() {
      if(this.countries) {
        const currentCountry = this.countries[this.country];
        return Object.keys(currentCountry.states).map((abbr) => { 
                 return {
                   text: `${abbr} | ${currentCountry.states[abbr] || '???'}`, 
                   value: abbr
                 } 
               })
      } else {
        return {};
      }
    }
  },
  methods: {
    ...mapActions({
      getCountries: 'getCountries',
    }),
    mappedSum(attr) {
      return Math.round(this.results.map( e => attr(e) ).reduce( (a,v) => a + v, 0) * 100) / 100;
    },
    getResults() {
      this.showSpinner()
      axios.get(`${BASE_API}/fishbowl/order/by_location`, {params: {
        start: this.start, 
        finish: this.finish, 
        country: this.country, 
        state: this.state
      }}).then( (res) => {
        this.results = res.data;
        this.hideSpinner()
      });
    },
    handleChangeCountry(event) {
      this.addressData.region = null;
      this.$forceUpdate();
    },
    showSpinner: function () {
      this.$refs.statusModalRef.show();
    },
    hideSpinner: function () {
      this.$refs.statusModalRef.hide();
    },
  },
   mounted() {
    this.getCountries();
  }
}
</script>



// WEBPACK FOOTER //
// src/components/sales-order/reports/SalesByLocation.vue